import * as Sentry from '@sentry/react'
import { Suspense } from 'react'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router'

import { AuthLogoutDialog } from '@/components/dialogs/auth/logout'
import { ReactRouterErrorElement } from '@/components/error/react-router-error-element'
import { NavigationProgress } from '@/components/navigation-progress'
import { ForceLogoutBridge } from '@/contexts/auth/force-logout-bridge'
import { withNamespaces } from '@/i18n/route-namespaces'
import { LoginPage } from '@/pages/auth/login'

import { AuthLayout } from '../pages/auth/layout'
import { botRoutes } from './bot'
import { companiesRoute } from './companies'
import { companyRoutes } from './company'
import { SessionLoaderErrorElement } from './error-elements/session-loader-error-element'
import { SessionAuthRouteGuard } from './guards/session-auth'
import { sessionLoader } from './loaders/session'
import { projectRoutes } from './project'
import { ProjectRoutesWrapper } from './project/root'
import { RoutesRootWrapper } from './root'
import { RouteFallback } from './route-fallback'

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

const importForgotPassword = () => import('../pages/auth/forgot-password')

function RootLayout() {
  return (
    <>
      <NavigationProgress />
      <ForceLogoutBridge />
      <AuthLogoutDialog />
      <Outlet />
    </>
  )
}

const appRoutes = [
  {
    path: '/',
    element: <Navigate to="/companies" replace />,
    errorElement: <ReactRouterErrorElement />,
  },

  {
    loader: sessionLoader,
    errorElement: <SessionLoaderErrorElement />,
    element: <SessionAuthRouteGuard />,
    children: [
      {
        errorElement: <ReactRouterErrorElement />,
        children: [
          companiesRoute,
          {
            element: <RoutesRootWrapper />,
            children: [
              companyRoutes,
              {
                element: <ProjectRoutesWrapper />,
                children: [projectRoutes, botRoutes],
              },
            ],
          },
        ],
      },
    ],
  },

  {
    element: <AuthLayout />,
    errorElement: <ReactRouterErrorElement />,
    children: [
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'sign-up',
        lazy: async () => {
          const { SignUpPage } = await import('@/pages/auth/sign-up')

          return { Component: SignUpPage }
        },
      },
      {
        path: 'forgot-password',
        lazy: async () => {
          const { ForgotPasswordPage } = await importForgotPassword()

          return { Component: ForgotPasswordPage }
        },
      },
      {
        path: 'create-password',
        lazy: async () => {
          const { ForgotPasswordPage } = await importForgotPassword()

          return { Component: ForgotPasswordPage }
        },
      },
    ],
  },

  {
    path: 'oauth',
    lazy: async () => {
      const { OAuthRedirect } = await import('../pages/auth/oauth')

      return { Component: OAuthRedirect }
    },
    errorElement: <ReactRouterErrorElement />,
  },

  {
    path: 'connect',
    lazy: async () => {
      const { ConnectPage } = await import('../pages/auth/connect')

      return { Component: ConnectPage }
    },
    errorElement: <ReactRouterErrorElement />,
  },

  {
    path: '*',
    lazy: withNamespaces(['notFound'], async () => {
      const { NotFoundPage } = await import('@/pages/not-found')

      return { Component: NotFoundPage }
    }),
    errorElement: <ReactRouterErrorElement />,
  },
]

const router = sentryCreateBrowserRouter([
  {
    hydrateFallbackElement: <RouteFallback fullScreen />,
    element: (
      <Suspense fallback={<RouteFallback fullScreen />}>
        <RootLayout />
      </Suspense>
    ),
    children: appRoutes,
  },
])

export function AppRoutes() {
  return <RouterProvider router={router} />
}
